import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import api from '../utils/api';
import { fmt, fmtDate, fmtTime } from '../utils/format';
import { useAuth } from '../context/AuthContext';
import Pager from '../components/Pager';
import InvoiceViewModal from '../components/InvoiceViewModal';
import { printInvoice } from '../utils/printInvoice';
import Alert from '../components/Alert';

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [createdBy, setCreatedBy] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewInvoiceId, setViewInvoiceId] = useState(null);
  const { isAdmin } = useAuth();

  // Debounce search → reset page
  useEffect(() => {
    const t = setTimeout(() => {
      setAppliedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // Load users for admin filter
  useEffect(() => {
    if (!isAdmin) return;
    api.get('/users').then(res => setUsers(res.data.users)).catch(() => {});
  }, [isAdmin]);

  // Fetch on any filter / page change
  useEffect(() => {
    setLoading(true);
    const params = { page };
    if (appliedSearch.trim()) params.search = appliedSearch.trim();
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    if (createdBy) params.createdBy = createdBy;
    api.get('/invoices', { params })
      .then(res => {
        setInvoices(res.data.invoices);
        setTotal(res.data.total || 0);
        setPages(res.data.pages || 1);
      })
      .catch(() => setError('Could not load invoices.'))
      .finally(() => setLoading(false));
  }, [appliedSearch, dateFrom, dateTo, createdBy, page]);

  function handleDateFrom(v) { setDateFrom(v); setPage(1); }
  function handleDateTo(v) { setDateTo(v); setPage(1); }
  function handleCreatedBy(v) { setCreatedBy(v); setPage(1); }

  function clearFilters() {
    setSearch('');
    setAppliedSearch('');
    setDateFrom('');
    setDateTo('');
    setCreatedBy('');
    setPage(1);
  }

  async function handlePrint(id) {
    const res = await api.get(`/invoices/${id}`);
    printInvoice(res.data.invoice);
  }

  const hasFilters = search || dateFrom || dateTo || createdBy;

  return (
    <Layout title="Invoices">
      <Alert type="danger" message={error} onClose={() => setError('')} />

      <div style={{ display: 'flex', gap: 8, marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">Search</label>
          <input
            style={{ width: 240 }}
            placeholder="Invoice # or customer…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">From date</label>
          <input type="date" value={dateFrom} max={dateTo || undefined} onChange={e => handleDateFrom(e.target.value)} />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">To date</label>
          <input type="date" value={dateTo} min={dateFrom || undefined} onChange={e => handleDateTo(e.target.value)} />
        </div>
        {isAdmin && (
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Created by</label>
            <select value={createdBy} onChange={e => handleCreatedBy(e.target.value)} style={{ minWidth: 160 }}>
              <option value="">All users</option>
              {users.map(u => <option key={u.id} value={u.name}>{u.name}</option>)}
            </select>
          </div>
        )}
        {hasFilters && (
          <button className="btn" onClick={clearFilters}>
            <i className="ti ti-x" aria-hidden="true"></i>Clear
          </button>
        )}
      </div>

      <div className="card">
        {loading ? (
          <div className="empty-state">Loading invoices…</div>
        ) : invoices.length === 0 ? (
          <div className="empty-state">
            <i className="ti ti-file-off" aria-hidden="true"></i>No invoices found
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Customer</th>
                <th>Date</th>
                <th>Time</th>
                {isAdmin && <th>Created By</th>}
                <th>Items</th>
                <th>Grand Total</th>
                <th>Balance</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(i => (
                <tr key={i.id}>
                  <td><span style={{ color: 'var(--green)', fontWeight: 600 }}>{i.invoice_number}</span></td>
                  <td>{i.customer_name || 'Walk-in'}</td>
                  <td>{fmtDate(i.invoice_date)}</td>
                  <td>{fmtTime(i.invoice_date)}</td>
                  {isAdmin && <td>{i.created_by_name}</td>}
                  <td>{i.item_count}</td>
                  <td><strong>{fmt(i.total)}</strong></td>
                  <td>
                    {parseFloat(i.balance_due) > 0 ? (
                      <span className="badge badge-red">{fmt(i.balance_due)} due</span>
                    ) : (
                      <span className="badge badge-green">Paid</span>
                    )}
                  </td>
                  <td>
                    <button className="btn btn-sm btn-primary" onClick={() => setViewInvoiceId(i.id)}>
                      <i className="ti ti-eye" aria-hidden="true"></i>View
                    </button>
                    &nbsp;
                    <button className="btn btn-sm" onClick={() => handlePrint(i.id)}>
                      <i className="ti ti-printer" aria-hidden="true"></i>Print
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Pager page={page} pages={pages} total={total} onPage={setPage} label="invoices" />

      {viewInvoiceId && <InvoiceViewModal invoiceId={viewInvoiceId} onClose={() => setViewInvoiceId(null)} />}
    </Layout>
  );
}
